import { useCallback, useReducer, useRef } from 'react';
import { ApiError, continueChat, reconnectChatStream, startChat, stopChat } from '../api/chat';
import { SigningInAgainError } from '../api/http';
import { fetchSessionHistory } from '../api/sessions';
import { trackPendingSend } from '../auth/pendingSend';
import type { Choice, Citation, ImageRef, StatusEventData } from '../types/api';
import { type ProgressEvent, type ProgressStep, toSteps } from '../utils/progress';

// Sent when a screenshot is attached with no question of its own. Names no product:
// the request carries the product and every agent prompt is scoped by it, so naming
// one here only ever contradicts the selection.
const DEFAULT_IMAGE_QUESTION =
  "Identify what this screenshot shows (which dialog, panel, or menu it's from) and explain what it's for and how to use it.";

// Earlier wording, kept so conversations sent before it changed still collapse to the
// "Attached N images" placeholder instead of showing the question back to the user.
const LEGACY_IMAGE_QUESTIONS = [
  "Identify what this screenshot shows in MIKE+ (which dialog, panel, or menu it's from) and explain what it's for and how to use it.",
];

// Marker prefix the backend splices onto the outgoing query per attachment
// (see api/chat.ts). Display must never show the raw caption text past this
// point — it reveals internal image-processing detail to the user.
const IMAGE_DESCRIPTION_MARKER = '\n\n[Image description: ';

function attachedImagesLabel(count: number): string {
  return `Attached ${count} image${count > 1 ? 's' : ''}`;
}

// Strips the spliced `[Image description: ...]` blocks a persisted message
// carries, falling back to the "Attached N images" placeholder when nothing
// but the default question (or no text at all) remains.
function displayContentFor(content: string, imageRefs: ImageRef[] | null | undefined): string {
  if (!imageRefs || imageRefs.length === 0) return content;
  const markerIndex = content.indexOf(IMAGE_DESCRIPTION_MARKER);
  const stripped = markerIndex === -1 ? content : content.slice(0, markerIndex);
  return stripped.trim().length === 0 ||
    stripped === DEFAULT_IMAGE_QUESTION ||
    LEGACY_IMAGE_QUESTIONS.includes(stripped)
    ? attachedImagesLabel(imageRefs.length)
    : stripped;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  traceId?: string;
  status?: 'in_progress' | 'done' | 'error' | 'stopped';
  statusEvents?: StatusEventData[];
  /** The progress log of this answer (#474): steps from the stream, or from history. */
  progress?: ProgressStep[];
  imageRefs?: ImageRef[];
  /** The options a setup question offers to click (#489). */
  choices?: Choice[];
}

export type ChatStatus = 'idle' | 'pending' | 'routing' | 'retrieving' | 'generating' | 'error';

// One concurrent documentation_retrieval goal within the current planner
// iteration. Sibling goals in the same iteration all start together but
// resolve independently (see backend/core/assistant.py's as_completed loop).
export interface ActiveSearch {
  id: string;
  message: string;
  status: 'pending' | 'done' | 'failed';
}

export interface ChatState {
  messages: ChatMessage[];
  status: ChatStatus;
  statusMessage: string;
  activeSearches: ActiveSearch[];
  /** The in-flight answer's progress log (#474). */
  progress: ProgressStep[];
  streamingContent: string;
  streamingCitations: Citation[];
  /** The in-flight reply's options, attached to the message when it is done. */
  streamingChoices: Choice[];
  product: string | null;
  /** The docs version the conversation is pinned to; null until known. */
  version: string | null;
  error: string | null;
  completedCount: number;
  isLoadingSession: boolean;
}

type Action =
  | { type: 'RESET' }
  | { type: 'START_SESSION_LOAD' }
  | {
      type: 'LOAD_HISTORY';
      messages: ChatMessage[];
      product: string | null;
      version?: string | null;
    }
  | { type: 'ADD_USER_MSG'; message: ChatMessage; product: string | null; version?: string | null }
  | {
      type: 'STATUS';
      stage: string;
      statusMessage: string;
      searchId?: string;
      searchDone?: boolean;
      searchFailed?: boolean;
      step?: ProgressEvent;
    }
  | { type: 'CONTEXT'; citations: Citation[] }
  | { type: 'CHOICES'; choices: Choice[] }
  | { type: 'TOKEN'; text: string }
  | { type: 'DONE'; traceId?: string }
  | { type: 'ERROR'; message: string }
  | { type: 'STOPPED' };

export const initialState: ChatState = {
  messages: [],
  status: 'idle',
  statusMessage: '',
  activeSearches: [],
  progress: [],
  streamingContent: '',
  streamingCitations: [],
  streamingChoices: [],
  // No product until a session is loaded or a message is sent. The field is only read
  // once a conversation has messages, and naming one here made a fresh conversation
  // claim a product the user had not picked.
  product: null,
  version: null,
  error: null,
  completedCount: 0,
  isLoadingSession: false,
};

// Sessions created before the backend persisted a product carry none. They can only
// be MIKE+ conversations: nothing else was live yet.
const PRE_MULTI_PRODUCT_SESSION_PRODUCT = 'mike-plus';

const STATUS_MAP: Record<string, ChatStatus> = {
  routing: 'routing',
  retrieving: 'retrieving',
  generating: 'generating',
};

export function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'RESET':
      return initialState;

    case 'START_SESSION_LOAD':
      // Preserve product for the brief window before LOAD_HISTORY lands —
      // it's about to be overwritten with the session's actual persisted
      // product anyway, this just avoids a flash of the wrong value.
      return {
        ...initialState,
        product: state.product,
        version: state.version,
        isLoadingSession: true,
      };

    case 'LOAD_HISTORY':
      // The backend now persists which product a session was created with
      // (frozen at the first message) — always trust that over whatever was
      // in state, otherwise switching sessions in the sidebar (which doesn't
      // reset chat state first) leaves the previous session's product
      // showing as locked. Older sessions predating this persistence have no
      // stored product; those are mike-plus by fact rather than by fallback, since
      // it was the only product that existed while they were created.
      return {
        ...initialState,
        product: action.product ?? PRE_MULTI_PRODUCT_SESSION_PRODUCT,
        // Sessions from before versions were stored carry none; the label then
        // just names the product.
        version: action.version ?? null,
        messages: action.messages,
        isLoadingSession: false,
      };

    case 'ADD_USER_MSG':
      return {
        ...state,
        messages: [...state.messages, action.message],
        status: 'pending',
        statusMessage: '',
        activeSearches: [],
        progress: [],
        streamingContent: '',
        streamingCitations: [],
        streamingChoices: [],
        product: action.product ?? state.product,
        version: action.version ?? state.version,
        error: null,
      };

    case 'STATUS': {
      const base = {
        ...state,
        status: STATUS_MAP[action.stage] ?? state.status,
        statusMessage: action.statusMessage,
        progress: [...state.progress, ...toSteps(action.step ? [action.step] : [])],
      };
      // Non-search stages (routing/searching-found/generating) close out
      // whatever search batch was showing.
      if (action.searchId === undefined) {
        return { ...base, activeSearches: [] };
      }
      if (action.searchDone) {
        return {
          ...base,
          activeSearches: state.activeSearches.map((s) =>
            s.id === action.searchId
              ? { ...s, status: action.searchFailed ? 'failed' : 'done' }
              : s,
          ),
        };
      }
      // A new search-start: if every entry so far is already resolved, this is
      // a fresh iteration's batch — start over instead of accumulating stale
      // (already-cleared-from-the-UI) chips from an earlier iteration.
      const allResolved = state.activeSearches.every((s) => s.status !== 'pending');
      const carried = allResolved ? [] : state.activeSearches;
      return {
        ...base,
        activeSearches: [
          ...carried,
          { id: action.searchId, message: action.statusMessage, status: 'pending' },
        ],
      };
    }

    case 'CONTEXT':
      return {
        ...state,
        streamingCitations: action.citations,
      };

    case 'CHOICES':
      return { ...state, streamingChoices: action.choices };

    case 'TOKEN':
      return {
        ...state,
        status: 'generating',
        streamingContent: state.streamingContent + action.text,
      };

    case 'DONE': {
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: state.streamingContent,
        citations: state.streamingCitations.length > 0 ? state.streamingCitations : undefined,
        traceId: action.traceId,
        progress: state.progress.length > 0 ? state.progress : undefined,
        choices: state.streamingChoices.length > 0 ? state.streamingChoices : undefined,
      };
      return {
        ...state,
        messages: [...state.messages, assistantMsg],
        status: 'idle',
        statusMessage: '',
        activeSearches: [],
        // streamingContent and streamingCitations are intentionally kept here so
        // StreamingMessage can finish its typewriter drain before transitioning
        // to FinishedMessage. Both are cleared by the next ADD_USER_MSG action.
        error: null,
        completedCount: state.completedCount + 1,
      };
    }

    case 'ERROR':
      return {
        ...state,
        status: 'error',
        error: action.message,
        streamingContent: '',
        streamingCitations: [],
        streamingChoices: [],
        statusMessage: '',
        activeSearches: [],
      };

    case 'STOPPED': {
      const stoppedMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        status: 'stopped',
      };
      return {
        ...state,
        messages: [...state.messages, stoppedMsg],
        status: 'idle',
        statusMessage: '',
        activeSearches: [],
        streamingContent: '',
        streamingCitations: [],
        streamingChoices: [],
        error: null,
      };
    }

    default:
      return state;
  }
}

const DEFAULT_ERROR = 'Sorry, something went wrong. Please try again.';

/**
 * Backend rejections whose own message is written for the user and says something the
 * generic failure cannot. All three are cases where "Please try again" is actively the
 * wrong instruction: retrying immediately is what tripped the ceiling, retrying a send
 * while the previous one is still answering is what the 409 is refusing, and a product
 * withdrawn from this account will not come back by retrying.
 *
 * An allowlist rather than "use whatever the server said", because `detail` is not
 * always ours to render: a validation error makes it an array of objects, and a proxy
 * in front of the app can return HTML with no `detail` at all.
 */
const SURFACED_ERROR_CODES = new Set([
  'rate_limited',
  'already_generating',
  'product_unavailable',
  'version_unavailable',
]);

/** Exported for its unit tests. */
export function errorMessageFor(err: unknown): string {
  if (err instanceof ApiError && err.code && SURFACED_ERROR_CODES.has(err.code)) {
    return err.message;
  }
  return DEFAULT_ERROR;
}

export function useChat() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (
      sessionId: string,
      query: string,
      product: string | null,
      version: string | null,
      isNew: boolean,
      attachments: ImageRef[] = [],
    ) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;

      const hasOnlyImages = query.trim().length === 0 && attachments.length > 0;
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: hasOnlyImages ? attachedImagesLabel(attachments.length) : query,
        imageRefs: attachments.length > 0 ? attachments : undefined,
      };
      dispatch({ type: 'ADD_USER_MSG', message: userMsg, product, version });

      // Captioning happens server-side, at send time — the frontend never sees
      // or composes caption text (see api/routes/chat.py's compose step).
      const effectiveQuery = hasOnlyImages ? DEFAULT_IMAGE_QUESTION : query;

      const body = {
        session_id: sessionId,
        query: effectiveQuery,
        product,
        // Only a new conversation chooses its version; a follow-up uses the one
        // the server pinned, whatever is sent.
        ...(isNew && version ? { version } : {}),
        ...(attachments.length > 0 ? { image_refs: attachments } : {}),
      };
      // Kept until the backend answers, so a page that leaves for the silent sign-in in
      // between can send it again when it comes back. See auth/pendingSend.ts.
      const stream = trackPendingSend(
        { sessionId, query, product, version, isNew },
        isNew ? startChat(body, signal) : continueChat(body, signal),
      );

      try {
        for await (const event of stream) {
          switch (event.event) {
            case 'status':
              dispatch({
                type: 'STATUS',
                stage: event.data.stage,
                statusMessage: event.data.message,
                searchId: event.data.search_id ?? undefined,
                searchDone: event.data.search_done,
                searchFailed: event.data.search_failed,
                step: event.data,
              });
              break;
            case 'context':
              dispatch({ type: 'CONTEXT', citations: event.data.citations });
              break;
            case 'choices':
              dispatch({ type: 'CHOICES', choices: event.data.choices });
              break;
            case 'token':
              dispatch({ type: 'TOKEN', text: event.data.text });
              break;
            case 'done':
              dispatch({ type: 'DONE', traceId: event.data.trace_id ?? undefined });
              break;
            case 'error':
              dispatch({ type: 'ERROR', message: DEFAULT_ERROR });
              break;
          }
        }
      } catch (err) {
        // A deadline expiring lands here as a TimeoutError rather than an AbortError,
        // which is the whole point of that class: an AbortError means the user pressed
        // stop and returns silently. A timeout still shows the generic failure; only the
        // codes in SURFACED_ERROR_CODES get a message of their own.
        // The page is leaving to sign in again and the message goes with it, so this one
        // keeps it, and the page stays as it is until the navigation.
        if (err instanceof SigningInAgainError) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        dispatch({ type: 'ERROR', message: errorMessageFor(err) });
      }
    },
    [],
  );

  const stopGeneration = useCallback(async (sessionId: string) => {
    abortRef.current?.abort();
    dispatch({ type: 'STOPPED' });
    try {
      await stopChat(sessionId);
    } catch {
      /* fire-and-forget */
    }
  }, []);

  const loadSession = useCallback(async (sessionId: string) => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'START_SESSION_LOAD' });
    const history = await fetchSessionHistory(sessionId);

    // Separate the synthetic in-progress assistant placeholder (if any) from
    // completed messages. The placeholder is added by the backend when a turn is
    // still generating; we strip it here and reconnect to the live stream instead.
    const hasInProgress = history.messages.some(
      (m) => m.role === 'assistant' && m.status === 'in_progress',
    );
    const completedMessages = hasInProgress
      ? history.messages.filter((m) => m.status !== 'in_progress')
      : history.messages;

    dispatch({
      type: 'LOAD_HISTORY',
      product: history.product ?? null,
      version: history.version ?? null,
      messages: completedMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: displayContentFor(m.content, m.image_refs),
        citations: m.citations ?? undefined,
        traceId: m.role === 'assistant' ? m.id.replace(/-/g, '') : undefined,
        imageRefs: m.image_refs ?? undefined,
        status: m.status as ChatMessage['status'],
        statusEvents: m.status_events ?? undefined,
        progress: toSteps(m.status_events),
        choices: m.choices ?? undefined,
      })),
    });

    if (!hasInProgress) return;

    // Reconnect to the live generation stream — replay past events then receive
    // future ones. A 404 means generation already finished; the polling in
    // useSessions will trigger another loadSession call when it detects completion.
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      for await (const event of reconnectChatStream(sessionId, controller.signal)) {
        switch (event.event) {
          case 'status':
            dispatch({
              type: 'STATUS',
              stage: event.data.stage,
              statusMessage: event.data.message,
              searchId: event.data.search_id ?? undefined,
              searchDone: event.data.search_done,
              searchFailed: event.data.search_failed,
              step: event.data,
            });
            break;
          case 'context':
            dispatch({ type: 'CONTEXT', citations: event.data.citations });
            break;
          case 'choices':
            dispatch({ type: 'CHOICES', choices: event.data.choices });
            break;
          case 'token':
            dispatch({ type: 'TOKEN', text: event.data.text });
            break;
          case 'done':
            dispatch({ type: 'DONE', traceId: event.data.trace_id ?? undefined });
            break;
          case 'error':
            dispatch({ type: 'ERROR', message: DEFAULT_ERROR });
            break;
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      // 404 or network error: generation already finished, polling will reload
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'RESET' });
  }, []);

  return {
    messages: state.messages,
    status: state.status,
    statusMessage: state.statusMessage,
    activeSearches: state.activeSearches,
    progress: state.progress,
    streamingContent: state.streamingContent,
    streamingCitations: state.streamingCitations,
    product: state.product,
    version: state.version,
    error: state.error,
    completedCount: state.completedCount,
    isLoadingSession: state.isLoadingSession,
    sendMessage,
    stopGeneration,
    loadSession,
    reset,
  };
}
